import React, { useCallback, useEffect, useState } from 'react';
import { LifeBuoy, Plus, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { clientGet, errorMessage, isUnauthorized, fmtShortDate } from '../../lib/clientApi';
import { StatusBadge, EmptyState } from './shared';
import { TicketDialog } from './TicketDialog';

export function ClientTickets({ onUnauthorized }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await clientGet('/api/client/tickets');
      setItems(res.data.items || []);
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      setError(errorMessage(err, 'Impossible de charger vos tickets'));
    } finally { setLoading(false); }
  }, [onUnauthorized]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="client-tickets">
      <div className="flex items-center gap-2">
        <h1 className="font-['Space_Grotesk'] text-2xl font-semibold tracking-tight">Mes tickets</h1>
        <Button variant="ghost" size="icon" className="ml-auto h-11 w-11" onClick={load} aria-label="Actualiser" data-testid="client-tickets-refresh"><RefreshCw className="h-4 w-4" /></Button>
        <Button className="h-11" onClick={() => setOpen(true)} data-testid="client-ticket-new-button"><Plus className="h-4 w-4 mr-1" /> Nouveau ticket</Button>
      </div>
      <p className="text-base text-muted-foreground">Une question sur une facture, une intervention ou votre planning ? Déposez une demande : l'agence la traite directement.</p>

      {loading && <div className="space-y-3">{[0, 1].map((i) => <Skeleton key={i} className="h-24 rounded-[var(--radius)]" />)}</div>}

      {!loading && error && (
        <Card className="p-4 border-destructive/30" role="alert" data-testid="client-tickets-error">
          <p className="text-base">{error}</p>
          <Button variant="outline" className="mt-3 h-11" onClick={load} data-testid="client-tickets-retry">Réessayer</Button>
        </Card>
      )}

      {!loading && !error && items.length === 0 && (
        <EmptyState icon={LifeBuoy} title="Aucun ticket" copy="Vous pouvez déposer une demande si vous avez une question ou une réclamation." action={<Button className="h-11" onClick={() => setOpen(true)} data-testid="client-tickets-empty-new-button"><Plus className="h-4 w-4 mr-1" /> Nouveau ticket</Button>} testId="client-tickets-empty-state" />
      )}

      {!loading && items.map((t) => (
        <Card key={t.id} className="p-4 shadow-sm border-border" data-testid="client-ticket-card">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-base font-semibold" data-testid="client-ticket-subject">{t.subject}</p>
              <p className="text-sm text-muted-foreground mt-0.5">{t.category_label} · {t.ref || 'en cours d\'enregistrement'} · déposé le {fmtShortDate(t.created_at)}</p>
            </div>
            <StatusBadge state={t.state === 'in_progress' ? 'in_progress' : t.state} label={t.status_label} testId="client-ticket-status-badge" />
          </div>
          <p className="text-base text-muted-foreground mt-2 line-clamp-3 whitespace-pre-line">{t.message}</p>
        </Card>
      ))}

      <TicketDialog open={open} onClose={() => setOpen(false)} onDone={() => { setOpen(false); toast.success('Votre demande a bien été transmise à l\'agence'); load(); }} onUnauthorized={onUnauthorized} />
    </div>
  );
}
